import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { getCartToken } from "@faire/retailer-visitor-shared/serialized-data/getCartToken";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import fetchCart from "@faire/web-api--source/endpoints/www/api/v2/carts/cartToken/get";
import {
  useInvalidateQuery as useInvalidateCartQuery,
  getQueryKey as getCartQueryKey,
} from "@faire/web-api--source/endpoints/www/api/v2/carts/cartToken/get-hooks";
import { ICartV2 } from "@faire/web-api--source/faire/carts/ICartV2";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { getCartQueryParams } from "./consts";

export const BRAND_CART_STALE_TIME = 10 * 1000; // 10 seconds

type BuildQueryParamsArg = {
  brandToken: string;
};

export const useBrandCartQueryParams = () => {
  return useCallback(({ brandToken }: BuildQueryParamsArg) => {
    return getCartQueryParams(brandToken);
  }, []);
};

export const useBrandCart = (brandToken: string) => {
  const buildQueryParams = useBrandCartQueryParams();
  const isLoggedInRetailer = useIsLoggedInRetailer();

  const queryResult = useQuery({
    queryKey: getCartQueryKey(getCartToken(), buildQueryParams({ brandToken })),
    queryFn: () => {
      const params = buildQueryParams({ brandToken });
      return fetchCart(getCartToken(), params);
    },
    refetchOnMount: false,
    placeholderData: keepPreviousData,
    enabled: isLoggedInRetailer && !!brandToken,
    staleTime: BRAND_CART_STALE_TIME,
  });

  const data = useMemo(() => {
    return ICartV2.build({
      brand_carts: queryResult.data?.brand_carts?.filter(isNotUndefined),
      filter_sections:
        queryResult.data?.filter_sections?.filter(isNotUndefined),
      cart_stats: queryResult.data?.cart_stats,
    });
  }, [queryResult.data]);

  return {
    ...queryResult,
    data,
    refetch: queryResult.refetch,
    isLoading: queryResult.isLoading,
  };
};

export const useInvalidateBrandCart = () => {
  const buildQueryParams = useBrandCartQueryParams();
  const invalidateCartQuery = useInvalidateCartQuery();

  const invalidateCartRequest = useCallback(
    async ({ brandToken }: { brandToken: string }) => {
      const args = [
        getCartToken(),
        buildQueryParams({ brandToken }),
        undefined,
        { refetchType: "all" },
      ] as const;
      await Promise.all([invalidateCartQuery(...args)]);
    },
    [buildQueryParams, invalidateCartQuery]
  );

  const invalidateBrandCart = useCallback(
    async ({ brandToken }: { brandToken: string }) => {
      const promises: Promise<void>[] = [];
      promises.push(invalidateCartRequest({ brandToken }));
      await Promise.all(promises);
    },
    [invalidateCartRequest]
  );

  return {
    invalidateBrandCart,
  };
};
